import type { OrganizationWithMetadata } from '@/lib/domain/organization';
import type { OrganizationMemberWithProfile } from '@/lib/domain/organizationMember';
import type { OrganizationInvite } from '@/lib/domain/organizationInvite';
import { buildApiUrl, secureFetch } from '@/lib/queryClient';

interface ApiResponse<T> {
  data?: T;
  error?: string;
}

export interface Team {
  id: string;
  organization_id: string;
  name: string;
  description: string | null;
  created_at: string;
  updated_at: string;
  member_count?: number;
}

export interface TeamMember {
  id: string;
  team_id: string;
  user_id: string;
  created_at: string;
  profile?: {
    full_name: string | null;
    email: string | null;
    avatar_url: string | null;
  };
}

export interface PathwayPermission {
  id: string;
  organization_id: string;
  pathway_id: string;
  user_id: string | null;
  team_id: string | null;
  created_at: string;
  pathway?: {
    id: string;
    title: string;
  };
  user?: {
    id: string;
    full_name: string | null;
    email: string | null;
  };
  team?: {
    id: string;
    name: string;
  };
}

export interface PaginatedMembersResponse {
  data: OrganizationMemberWithProfile[];
  total: number;
  page: number;
  pageSize: number;
}

export interface MemberListParams {
  page?: number;
  pageSize?: number;
  sortBy?: 'name' | 'role' | 'status' | 'joinedAt';
  sortDir?: 'asc' | 'desc';
  role?: string[];
  status?: string[];
  teamId?: string;
  search?: string;
}

export interface BulkActionPayload {
  action: 'change_role' | 'suspend' | 'assign_team';
  userIds: string[];
  payload: {
    role?: string;
    teamId?: string;
    reason?: string;
  };
}

export interface BulkActionResult {
  success: number;
  failed: number;
  errors: string[];
}

function normalizeOrganization(org: any): OrganizationWithMetadata {
  return {
    id: org.id,
    name: org.name,
    slug: org.slug,
    status: org.status || 'active',
    createdAt: org.created_at || org.createdAt,
    createdBy: org.created_by || org.createdBy,
    description: org.description,
    avatarUrl: org.avatar_url || org.avatarUrl,
    website: org.website,
    member_count: org.member_count,
    user_role: org.user_role || org.role,
  };
}

export interface PaginatedOrganizationsResponse {
  organizations: OrganizationWithMetadata[];
  total: number;
  limit: number;
  offset: number;
}

export const organizationsApi = {
  /**
   * Organizations for the signed-in user.
   *
   * `userId` is vestigial and ignored — the endpoint is `/api/organizations`
   * and scopes to the session, not to a path or query parameter. It is optional
   * so that callers reading a user out of auth state can pass `user?.id`
   * honestly, instead of asserting `user?.id!` and claiming a value they do not
   * have. Five call sites were doing exactly that.
   *
   * @deprecated Pass nothing. The parameter will be removed.
   */
  async getUserOrganizations(userId?: string, options?: { limit?: number; offset?: number }): Promise<ApiResponse<OrganizationWithMetadata[]>> {
    try {
      const params = new URLSearchParams();
      if (options?.limit) params.append('limit', String(options.limit));
      if (options?.offset) params.append('offset', String(options.offset));
      const url = params.toString() ? `/api/organizations?${params}` : '/api/organizations';
      
      const response = await secureFetch(buildApiUrl(url));
      if (!response.ok) throw new Error('Failed to fetch organizations');
      const data = await response.json();
      
      // Handle both array (legacy) and paginated object response
      const orgs = Array.isArray(data) ? data : (data.organizations || []);
      const normalized = orgs.map(normalizeOrganization);
      return { data: normalized, error: undefined };
    } catch (error) {
      return {
        data: undefined,
        error: error instanceof Error ? error.message : 'Failed to fetch organizations',
      };
    }
  },
  
  async getUserOrganizationsPaginated(userId: string, options?: { limit?: number; offset?: number }): Promise<ApiResponse<PaginatedOrganizationsResponse>> {
    try {
      const params = new URLSearchParams();
      if (options?.limit) params.append('limit', String(options.limit));
      if (options?.offset) params.append('offset', String(options.offset));
      const url = params.toString() ? `/api/organizations?${params}` : '/api/organizations';
      
      const response = await secureFetch(buildApiUrl(url));
      if (!response.ok) throw new Error('Failed to fetch organizations');
      const data = await response.json();
      
      // Handle both array (legacy) and paginated object response
      if (Array.isArray(data)) {
        return {
          data: {
            organizations: data.map(normalizeOrganization),
            total: data.length,
            limit: options?.limit || 20,
            offset: options?.offset || 0,
          },
          error: undefined,
        };
      }
      
      return {
        data: {
          organizations: (data.organizations || []).map(normalizeOrganization),
          total: data.total || 0,
          limit: data.limit || 20,
          offset: data.offset || 0,
        },
        error: undefined,
      };
    } catch (error) {
      return {
        data: undefined,
        error: error instanceof Error ? error.message : 'Failed to fetch organizations',
      };
    }
  },

  async getOrganizationMembers(organizationId: string): Promise<ApiResponse<OrganizationMemberWithProfile[]>> {
    try {
      const response = await secureFetch(buildApiUrl(`/api/organizations/${organizationId}/members`));
      if (!response.ok) throw new Error('Failed to fetch members');
      const result = await response.json();
      const members = Array.isArray(result) ? result : Array.isArray(result?.data) ? result.data : [];
      return { data: members as OrganizationMemberWithProfile[], error: undefined };
    } catch (error) {
      return {
        data: undefined,
        error: error instanceof Error ? error.message : 'Failed to fetch organization members',
      };
    }
  },

  async createOrganization(input: { name: string; slug?: string; created_by: string }): Promise<ApiResponse<OrganizationWithMetadata>> {
    try {
      const slug =
        input.slug ||
        input.name
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-|-$/g, '');

      const response = await secureFetch(buildApiUrl('/api/organizations'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: input.name, slug }),
      });

      if (!response.ok) throw new Error('Failed to create organization');
      const data = await response.json();
      return { data: normalizeOrganization(data), error: undefined };
    } catch (error) {
      console.error('[organizationsApi] Error creating organization:', error);
      return {
        data: undefined,
        error: error instanceof Error ? error.message : 'Failed to create organization',
      };
    }
  },

  async inviteMember(input: {
    organization_id: string;
    email: string;
    role: string;
    invited_by: string;
    teamId?: string;
  }): Promise<ApiResponse<OrganizationInvite>> {
    const url = `/api/organizations/${input.organization_id}/invites`;
    const body = {
      email: input.email,
      role: input.role,
      teamId: input.teamId || null,
    };
    
    console.log('[INVITE_API] Making fetch request', { url, body });
    
    try {
      const response = await secureFetch(buildApiUrl(url), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },

        body: JSON.stringify(body),
      });
      
      console.log('[INVITE_API] Response status:', response.status, response.statusText);

      if (!response.ok) {
        const errorText = await response.text();
        console.log('[INVITE_API] Error response body:', errorText);
        throw new Error('Failed to invite member');
      }
      const data = await response.json();
      console.log('[INVITE_API] Success response:', data);
      return { data, error: undefined };
    } catch (error) {
      console.error('[INVITE_API] Fetch error:', error);
      return {
        data: undefined,
        error: error instanceof Error ? error.message : 'Failed to invite member',
      };
    }
  },

  async updateOrganization(input: {
    id: string;
    name?: string;
    status?: string;
  }): Promise<ApiResponse<OrganizationWithMetadata>> {
    try {
      const response = await secureFetch(buildApiUrl(`/api/organizations/${input.id}`), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },

        body: JSON.stringify(input),
      });

      if (!response.ok) throw new Error('Failed to update organization');
      const data = await response.json();
      return { data, error: undefined };
    } catch (error) {
      return {
        data: undefined,
        error: error instanceof Error ? error.message : 'Failed to update organization',
      };
    }
  },

  async deleteOrganization(organizationId: string, confirmName: string): Promise<ApiResponse<void>> {
    try {
      const response = await secureFetch(buildApiUrl(`/api/organizations/${organizationId}`), {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },

        body: JSON.stringify({ confirmName }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.message || 'Failed to delete organization');
      }
      return { data: undefined, error: undefined };
    } catch (error) {
      return {
        data: undefined,
        error: error instanceof Error ? error.message : 'Failed to delete organization',
      };
    }
  },

  async updateMemberRole(organizationId: string, userId: string, role: string): Promise<ApiResponse<void>> {
    try {
      const response = await secureFetch(buildApiUrl(`/api/organizations/${organizationId}/members/${userId}`), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },

        body: JSON.stringify({ role }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to update member role');
      }
      return { data: undefined, error: undefined };
    } catch (error) {
      return {
        data: undefined,
        error: error instanceof Error ? error.message : 'Failed to update member role',
      };
    }
  },

  async removeMember(organizationId: string, userId: string): Promise<ApiResponse<void>> {
    try {
      const response = await secureFetch(buildApiUrl(`/api/organizations/${organizationId}/members/${userId}`), {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },

      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to remove member');
      }
      return { data: undefined, error: undefined };
    } catch (error) {
      return {
        data: undefined,
        error: error instanceof Error ? error.message : 'Failed to remove member',
      };
    }
  },

  async getOrganizationTeams(orgId: string): Promise<ApiResponse<Team[]>> {
    try {
      const response = await secureFetch(buildApiUrl(`/api/organizations/${orgId}/teams`), { credentials: 'include' });
      if (!response.ok) throw new Error('Failed to fetch teams');
      const teams = await response.json();
      return { data: teams || [], error: undefined };
    } catch (error) {
      return {
        data: undefined,
        error: error instanceof Error ? error.message : 'Failed to fetch teams',
      };
    }
  },

  async createTeam(orgId: string, data: { name: string; description?: string }): Promise<ApiResponse<Team>> {
    try {
      const response = await secureFetch(buildApiUrl(`/api/organizations/${orgId}/teams`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },

        body: JSON.stringify(data),
      });

      if (!response.ok) throw new Error('Failed to create team');
      const team = await response.json();
      return { data: team, error: undefined };
    } catch (error) {
      return {
        data: undefined,
        error: error instanceof Error ? error.message : 'Failed to create team',
      };
    }
  },

  async updateTeam(teamId: string, data: { name?: string; description?: string }): Promise<ApiResponse<Team>> {
    try {
      const response = await secureFetch(buildApiUrl(`/api/teams/${teamId}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },

        body: JSON.stringify(data),
      });

      if (!response.ok) throw new Error('Failed to update team');
      const team = await response.json();
      return { data: team, error: undefined };
    } catch (error) {
      return {
        data: undefined,
        error: error instanceof Error ? error.message : 'Failed to update team',
      };
    }
  },

  async deleteTeam(teamId: string): Promise<ApiResponse<void>> {
    try {
      const response = await secureFetch(buildApiUrl(`/api/teams/${teamId}`), {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },

      });

      if (!response.ok) throw new Error('Failed to delete team');
      return { data: undefined, error: undefined };
    } catch (error) {
      return {
        data: undefined,
        error: error instanceof Error ? error.message : 'Failed to delete team',
      };
    }
  },

  async getTeamMembers(teamId: string): Promise<ApiResponse<TeamMember[]>> {
    try {
      const response = await secureFetch(buildApiUrl(`/api/teams/${teamId}/members`), { credentials: 'include' });
      if (!response.ok) throw new Error('Failed to fetch team members');
      const result = await response.json();
      const members = Array.isArray(result) ? result : Array.isArray(result?.data) ? result.data : [];
      return { data: members as TeamMember[], error: undefined };
    } catch (error) {
      return {
        data: undefined,
        error: error instanceof Error ? error.message : 'Failed to fetch team members',
      };
    }
  },

  async addTeamMember(teamId: string, userId: string): Promise<ApiResponse<TeamMember>> {
    try {
      const response = await secureFetch(buildApiUrl(`/api/teams/${teamId}/members`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },

        body: JSON.stringify({ userId }),
      });

      if (!response.ok) throw new Error('Failed to add team member');
      const member = await response.json();
      return { data: member, error: undefined };
    } catch (error) {
      return {
        data: undefined,
        error: error instanceof Error ? error.message : 'Failed to add team member',
      };
    }
  },

  async removeTeamMember(teamId: string, userId: string): Promise<ApiResponse<void>> {
    try {
      const response = await secureFetch(buildApiUrl(`/api/teams/${teamId}/members/${userId}`), {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },

      });

      if (!response.ok) throw new Error('Failed to remove team member');
      return { data: undefined, error: undefined };
    } catch (error) {
      return {
        data: undefined,
        error: error instanceof Error ? error.message : 'Failed to remove team member',
      };
    }
  },

  async getPathwayPermissions(orgId: string): Promise<ApiResponse<PathwayPermission[]>> {
    try {
      const response = await secureFetch(buildApiUrl(`/api/organizations/${orgId}/pathways`), { credentials: 'include' });
      if (!response.ok) throw new Error('Failed to fetch pathway permissions');
      const permissions = await response.json();
      return { data: permissions || [], error: undefined };
    } catch (error) {
      return {
        data: undefined,
        error: error instanceof Error ? error.message : 'Failed to fetch pathway permissions',
      };
    }
  },

  async assignPathway(orgId: string, data: { pathwayId: string; userId?: string; teamId?: string }): Promise<ApiResponse<PathwayPermission>> {
    try {
      const response = await secureFetch(buildApiUrl(`/api/organizations/${orgId}/pathways/assign`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },

        body: JSON.stringify(data),
      });

      if (!response.ok) throw new Error('Failed to assign pathway');
      const permission = await response.json();
      return { data: permission, error: undefined };
    } catch (error) {
      return {
        data: undefined,
        error: error instanceof Error ? error.message : 'Failed to assign pathway',
      };
    }
  },

  async removePathwayPermission(permissionId: string): Promise<ApiResponse<void>> {
    try {
      const response = await secureFetch(buildApiUrl(`/api/pathways/permissions/${permissionId}`), {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },

      });

      if (!response.ok) throw new Error('Failed to remove pathway permission');
      return { data: undefined, error: undefined };
    } catch (error) {
      return {
        data: undefined,
        error: error instanceof Error ? error.message : 'Failed to remove pathway permission',
      };
    }
  },

  async getMemberTeams(orgId: string): Promise<ApiResponse<{ user_id: string; team_id: string; team_name: string }[]>> {
    try {
      const response = await secureFetch(buildApiUrl(`/api/organizations/${orgId}/member-teams`), { credentials: 'include' });
      if (!response.ok) throw new Error('Failed to fetch member teams');
      const memberTeams = await response.json();
      return { data: memberTeams || [], error: undefined };
    } catch (error) {
      return {
        data: undefined,
        error: error instanceof Error ? error.message : 'Failed to fetch member teams',
      };
    }
  },

  async assignMemberToTeam(orgId: string, userId: string, teamId: string | null): Promise<ApiResponse<void>> {
    try {
      const response = await secureFetch(buildApiUrl(`/api/organizations/${orgId}/members/${userId}/team`), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },

        body: JSON.stringify({ teamId }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to assign member to team');
      }
      return { data: undefined, error: undefined };
    } catch (error) {
      return {
        data: undefined,
        error: error instanceof Error ? error.message : 'Failed to assign member to team',
      };
    }
  },

  async getAssignablePathways(): Promise<ApiResponse<{ id: string; title: string; status: string }[]>> {
    try {
      const response = await secureFetch(buildApiUrl('/api/pathways/assignable'), { credentials: 'include' });
      if (!response.ok) throw new Error('Failed to fetch assignable pathways');
      const data = await response.json();
      return { data: data.pathways || [], error: undefined };
    } catch (error) {
      return {
        data: undefined,
        error: error instanceof Error ? error.message : 'Failed to fetch assignable pathways',
      };
    }
  },

  async getOrganizationAssignablePathways(orgId: string): Promise<ApiResponse<{ id: string; pathway_id: string; title: string; status: string }[]>> {
    try {
      const response = await secureFetch(buildApiUrl(`/api/organizations/${orgId}/pathways/assignable`), { credentials: 'include' });
      if (!response.ok) throw new Error('Failed to fetch organization assignable pathways');
      const data = await response.json();
      return { data: data.pathways || [], error: undefined };
    } catch (error) {
      return {
        data: undefined,
        error: error instanceof Error ? error.message : 'Failed to fetch organization assignable pathways',
      };
    }
  },

  async getAdminOrganizations(options?: { limit?: number; offset?: number; search?: string }): Promise<ApiResponse<{ organizations: OrganizationWithMetadata[]; total: number }>> {
    try {
      const params = new URLSearchParams();
      if (options?.limit) params.append('limit', String(options.limit));
      if (options?.offset) params.append('offset', String(options.offset));
      if (options?.search) params.append('search', options.search);
      const url = params.toString() ? `/api/admin/organizations?${params}` : '/api/admin/organizations';
      
      const response = await secureFetch(buildApiUrl(url));
      if (!response.ok) throw new Error('Failed to fetch organizations');
      const data = await response.json();
      
      return { 
        data: {
          organizations: (data.organizations || []).map(normalizeOrganization),
          total: data.total || 0,
        }, 
        error: undefined 
      };
    } catch (error) {
      return {
        data: undefined,
        error: error instanceof Error ? error.message : 'Failed to fetch organizations',
      };
    }
  },

  async getAdminOrganizationPathways(orgId: string): Promise<ApiResponse<{ id: string; title: string; slug: string; status: string; is_assignable_to_org: boolean; assignable_id: string | null }[]>> {
    try {
      const response = await secureFetch(buildApiUrl(`/api/admin/organizations/${orgId}/pathways`), { credentials: 'include' });
      if (!response.ok) throw new Error('Failed to fetch pathways');
      const data = await response.json();
      return { data: data.pathways || [], error: undefined };
    } catch (error) {
      return {
        data: undefined,
        error: error instanceof Error ? error.message : 'Failed to fetch pathways',
      };
    }
  },

  async exposePathwayToOrganization(orgId: string, pathwayId: string): Promise<ApiResponse<void>> {
    try {
      const response = await secureFetch(buildApiUrl(`/api/admin/organizations/${orgId}/pathways/${pathwayId}`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },

      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to expose pathway');
      }
      return { data: undefined, error: undefined };
    } catch (error) {
      return {
        data: undefined,
        error: error instanceof Error ? error.message : 'Failed to expose pathway',
      };
    }
  },

  async revokePathwayFromOrganization(orgId: string, pathwayId: string): Promise<ApiResponse<void>> {
    try {
      const response = await secureFetch(buildApiUrl(`/api/admin/organizations/${orgId}/pathways/${pathwayId}`), {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },

      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to revoke pathway');
      }
      return { data: undefined, error: undefined };
    } catch (error) {
      return {
        data: undefined,
        error: error instanceof Error ? error.message : 'Failed to revoke pathway',
      };
    }
  },

  async getOwnershipStatus(): Promise<ApiResponse<{ owns_org: boolean; organization_id?: string; organization_name?: string; allow_multi_org?: boolean }>> {
    try {
      const response = await secureFetch(buildApiUrl('/api/user/ownership-status'), { credentials: 'include' });
      if (!response.ok) throw new Error('Failed to fetch ownership status');
      const data = await response.json();
      return { data, error: undefined };
    } catch (error) {
      return {
        data: undefined,
        error: error instanceof Error ? error.message : 'Failed to fetch ownership status',
      };
    }
  },

  async transferOwnership(orgId: string, newOwnerId: string, confirmOrganizationName: string): Promise<ApiResponse<{ success: boolean; message: string }>> {
    try {
      const response = await secureFetch(buildApiUrl(`/api/organizations/${orgId}/transfer-ownership`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },

        body: JSON.stringify({ newOwnerId, confirmOrganizationName }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to transfer ownership');
      }
      const data = await response.json();
      return { data, error: undefined };
    } catch (error) {
      return {
        data: undefined,
        error: error instanceof Error ? error.message : 'Failed to transfer ownership',
      };
    }
  },

  async getOrganizationInvites(orgId: string): Promise<ApiResponse<OrganizationInvite[]>> {
    try {
      const response = await secureFetch(buildApiUrl(`/api/organizations/${orgId}/invites`), { credentials: 'include' });
      if (!response.ok) throw new Error('Failed to fetch invites');
      const invites = await response.json();
      return { data: invites || [], error: undefined };
    } catch (error) {
      return {
        data: undefined,
        error: error instanceof Error ? error.message : 'Failed to fetch invites',
      };
    }
  },

  async cancelInvite(orgId: string, inviteId: string): Promise<ApiResponse<void>> {
    try {
      const response = await secureFetch(buildApiUrl(`/api/organizations/${orgId}/invites/${inviteId}`), {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },

      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to cancel invite');
      }
      return { data: undefined, error: undefined };
    } catch (error) {
      return {
        data: undefined,
        error: error instanceof Error ? error.message : 'Failed to cancel invite',
      };
    }
  },

  async resendInvite(orgId: string, inviteId: string): Promise<ApiResponse<{ success: boolean; emailSent: boolean; inviteUrl?: string }>> {
    try {
      const response = await secureFetch(buildApiUrl(`/api/organizations/${orgId}/invites/${inviteId}/resend`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },

      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to resend invite');
      }
      const data = await response.json();
      return { data, error: undefined };
    } catch (error) {
      return {
        data: undefined,
        error: error instanceof Error ? error.message : 'Failed to resend invite',
      };
    }
  },

  async getOrganizationMembersPaginated(organizationId: string, params: MemberListParams): Promise<ApiResponse<PaginatedMembersResponse>> {
    try {
      const queryParams = new URLSearchParams();
      if (params.page !== undefined) queryParams.append('page', String(params.page));
      if (params.pageSize !== undefined) queryParams.append('pageSize', String(params.pageSize));
      if (params.sortBy) queryParams.append('sortBy', params.sortBy);
      if (params.sortDir) queryParams.append('sortDir', params.sortDir);
      if (params.role && params.role.length > 0) {
        params.role.forEach(r => queryParams.append('role[]', r));
      }
      if (params.status && params.status.length > 0) {
        params.status.forEach(s => queryParams.append('status[]', s));
      }
      if (params.teamId) queryParams.append('teamId', params.teamId);
      if (params.search) queryParams.append('search', params.search);

      const url = queryParams.toString()
        ? `/api/organizations/${organizationId}/members?${queryParams}`
        : `/api/organizations/${organizationId}/members`;

      const response = await secureFetch(buildApiUrl(url), { credentials: 'include' });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to fetch members');
      }
      const data = await response.json();
      if (data && Array.isArray(data.data)) {
        data.data = data.data.map((member: any) => {
          if (!member.profile) {
            const firstName = member.first_name || '';
            const lastName = member.last_name || '';
            const fullName = [firstName, lastName].filter(Boolean).join(' ') || null;
            member.profile = {
              full_name: fullName,
              email: member.email || null,
              avatar_url: member.profile_image_url || null,
            };
          }
          return member;
        });
      }
      return { data, error: undefined };
    } catch (error) {
      return {
        data: undefined,
        error: error instanceof Error ? error.message : 'Failed to fetch organization members',
      };
    }
  },

  async suspendMember(organizationId: string, userId: string, reason?: string): Promise<ApiResponse<void>> {
    try {
      const response = await secureFetch(buildApiUrl(`/api/organizations/${organizationId}/members/${userId}/suspend`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },

        body: JSON.stringify({ reason }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to suspend member');
      }
      return { data: undefined, error: undefined };
    } catch (error) {
      return {
        data: undefined,
        error: error instanceof Error ? error.message : 'Failed to suspend member',
      };
    }
  },

  async reactivateMember(organizationId: string, userId: string): Promise<ApiResponse<void>> {
    try {
      const response = await secureFetch(buildApiUrl(`/api/organizations/${organizationId}/members/${userId}/reactivate`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },

      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to reactivate member');
      }
      return { data: undefined, error: undefined };
    } catch (error) {
      return {
        data: undefined,
        error: error instanceof Error ? error.message : 'Failed to reactivate member',
      };
    }
  },

  async bulkMemberAction(organizationId: string, payload: BulkActionPayload): Promise<ApiResponse<BulkActionResult>> {
    try {
      const response = await secureFetch(buildApiUrl(`/api/organizations/${organizationId}/members/bulk`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },

        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to perform bulk action');
      }
      const data = await response.json();
      return { data, error: undefined };
    } catch (error) {
      return {
        data: undefined,
        error: error instanceof Error ? error.message : 'Failed to perform bulk action',
      };
    }
  },

  async leaveOrganization(organizationId: string): Promise<ApiResponse<void>> {
    try {
      const response = await secureFetch(buildApiUrl(`/api/organizations/${organizationId}/members/leave`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },

      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to leave organization');
      }
      return { data: undefined, error: undefined };
    } catch (error) {
      return {
        data: undefined,
        error: error instanceof Error ? error.message : 'Failed to leave organization',
      };
    }
  },

  async exportMembersCSV(organizationId: string): Promise<ApiResponse<string>> {
    try {
      const response = await secureFetch(buildApiUrl(`/api/organizations/${organizationId}/members/export`), { credentials: 'include' });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to export members');
      }
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      return { data: blobUrl, error: undefined };
    } catch (error) {
      return {
        data: undefined,
        error: error instanceof Error ? error.message : 'Failed to export members',
      };
    }
  },
};
